/* @flow */
/* eslint-disable import/max-dependencies */

import * as React from 'react';
import { I18nextProvider } from 'react-i18next';
import i18next from 'i18next';
import { Provider } from 'mobx-react';
import { Router, BrowserRouter } from 'react-router-dom';

import { MuiThemeProvider } from 'material-ui/styles';
import JssProvider from 'react-jss/lib/JssProvider';
import { createGenerateClassName } from '@material-ui/core/styles';

import {
  authenticationStore,
  getStores,
  inlineManualService,
  userStore,
  mrrEnhancedHistory,
} from 'services/ServiceRegistry';
import type { TypeStores } from 'services/ServiceRegistry';
import navigationService from 'services/NavigationService';
import Routing from 'views/Routing/Routing';
import LeftMenu from 'views/PageSections/Navigation/LeftMenu/LeftMenu';
import TopMenu from 'views/PageSections/Navigation/TopMenu/TopMenu';
import { AIErrorBoundary } from 'utils/applicationInsights';
import Loading from 'components/Loading/Loading';
import createI18n from 'i18nInstance';

inlineManualService.initialise(mrrEnhancedHistory, authenticationStore, userStore);
navigationService.init(mrrEnhancedHistory);

/**
 * @see @material-ui/core.com/customization/css-in-js/#class-names
 * @see @material-ui/core.com/customization/css-in-js/#global-css
 * @see @material-ui/core.com/customization/overrides/#global-css-override
 * @see @material-ui/core.com/guides/interoperability/#global-css
 * @see cssinjs.org/js-api/?v=v9.8.1#generate-your-own-class-names
 * @see github.com/mui-org/material-ui/blob/786046f/CHANGELOG.md#100-beta25
 */
const generateClassName = createGenerateClassName({ dangerouslyUseGlobalCSS: true });

type State = {
  i18nLoading: boolean,
  i18n?: typeof i18next,
  stores: TypeStores,
};

class Root extends React.Component<{}, State> {
  state: State = {
    i18nLoading: true,
    stores: getStores(),
  };

  constructor(props: *) {
    super(props);

    createI18n(userStore).then(i18n => this.setState({ i18nLoading: false, i18n }));
  }

  render() {
    const { i18n, i18nLoading, stores } = this.state;

    return (
      <AIErrorBoundary>
        <Provider {...stores}>
          <BrowserRouter
            getUserConfirmation={(message, callback) => {
              const allowTransition = window.confirm(message);
              callback(allowTransition);
            }}
          >
            <Router history={mrrEnhancedHistory}>
              <JssProvider generateClassName={generateClassName}>
                <MuiThemeProvider>
                  {i18nLoading ? (
                    <Loading />
                  ) : (
                    <I18nextProvider i18n={i18n}>
                      <React.Fragment>
                        <LeftMenu />
                        <TopMenu />
                        <Routing />
                      </React.Fragment>
                    </I18nextProvider>
                  )}
                </MuiThemeProvider>
              </JssProvider>
            </Router>
          </BrowserRouter>
        </Provider>
      </AIErrorBoundary>
    );
  }
}

export default Root;
